import React, { useState } from 'react'
import { useAdminAuth } from '../../middleware/adminAuth'
import { AdminSidebar } from './AdminSidebar'
import { DoctorRegistration } from './DoctorRegistration'
import { FarmRegistration } from './FarmRegistration'
import { BalanceManagement } from './BalanceManagement'
import { DoctorAiTokenManagement } from './DoctorAiTokenManagement'
import { DoctorList } from './DoctorList'
import { FarmList } from './FarmList'
import { FarmAITokens } from './FarmAITokens'
import { ManagePresets } from './ManagePresets'
import { logInfo } from '../../utils/errorLogger'
import './AdminDashboard.css'

export type AdminSection = 'register' | 'register-farm' | 'balance' | 'doctor-ai-tokens' | 'list' | 'farm-list' | 'farm-ai-tokens' | 'manage-presets'

interface AdminDashboardProps {
  onSwitchToCAD?: () => void
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onSwitchToCAD }) => {
  const { isAdmin, loading } = useAdminAuth()
  const [activeSection, setActiveSection] = useState<AdminSection>('register')

  // Loading state
  if (loading) {
    return (
      <div className="admin-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    )
  }

  // Non-admin users are redirected by useAdminAuth hook
  if (!isAdmin) {
    return null
  }

  const handleSectionChange = (section: AdminSection) => {
    logInfo(`Admin navigating to section: ${section}`, 'AdminDashboard')
    setActiveSection(section)
  }

  return (
    <div className="admin-dashboard">
      <AdminSidebar
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onSwitchToCAD={onSwitchToCAD}
      />

      <div className="admin-content">
        <div className="admin-content-header">
          <h1>{getSectionTitle(activeSection)}</h1>
          <p className="admin-content-subtitle">{getSectionSubtitle(activeSection)}</p>
        </div>

        <div className="admin-content-body">
          {activeSection === 'register' && (
            <div className="admin-section">
              <DoctorRegistration />
            </div>
          )}

          {activeSection === 'register-farm' && (
            <div className="admin-section">
              <FarmRegistration />
            </div>
          )}

          {activeSection === 'balance' && (
            <div className="admin-section">
              <BalanceManagement />
            </div>
          )}

          {activeSection === 'doctor-ai-tokens' && (
            <div className="admin-section">
              <DoctorAiTokenManagement />
            </div>
          )}

          {activeSection === 'list' && (
            <div className="admin-section">
              <DoctorList />
            </div>
          )}

          {activeSection === 'farm-list' && (
            <div className="admin-section">
              <FarmList />
            </div>
          )}

          {activeSection === 'farm-ai-tokens' && (
            <div className="admin-section">
              <FarmAITokens />
            </div>
          )}

          {activeSection === 'manage-presets' && (
            <div className="admin-section">
              <ManagePresets />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Helper functions
function getSectionTitle(section: AdminSection): string {
  switch (section) {
    case 'register':
      return 'Doctor Registration'
    case 'balance':
      return 'Balance Management'
    case 'doctor-ai-tokens':
      return 'User AI Tokens'
    case 'register-farm':
      return 'Farm Registration'
    case 'list':
      return 'Doctor List'
    case 'farm-ai-tokens':
      return 'Farm AI Tokens'
    case 'manage-presets':
      return 'Manage Presets'
    default:
      return 'Admin Dashboard'
  }
}

function getSectionSubtitle(section: AdminSection): string {
  switch (section) {
    case 'register':
      return 'Register new doctors to the Girasoles platform'
    case 'balance':
      return 'Manage doctor account balances and transactions'
    case 'doctor-ai-tokens':
      return 'Manage doctor AI token balances and transactions'
    case 'register-farm':
      return 'Register new print farms to the Girasoles platform'
    case 'list':
      return 'View and manage all registered doctors'
    case 'farm-ai-tokens':
      return 'Credit or debit AI token balances for print farms'
    case 'manage-presets':
      return 'Upload and manage printer presets for farms and users'
    default:
      return 'Manage doctors and platform settings'
  }
}

